import { DesktopNav, MobileNav, NavBarItem } from '@ds-mgnl/marketing';
import { ButtonLink, StandaloneIcon, classNames } from '@dsx/react';
import type { ReactNode, RefObject } from 'react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDisplayType } from '../../hooks';
import styles from './TopNav.module.css';
import { TopNavBarMobile } from './TopNavBarMobile';
import type { Logo, MenuLink } from './types';

const renderDesktopCTA = (ctaContent: MenuLink[]) => {
	if (ctaContent.length === 0) {
		return null;
	}

	return (
		<div className={styles['top-nav-cta']}>
			{ctaContent.map(({ path, value, icon, label, openInNewTab }, index) => (
				<ButtonLink
					key={path}
					href={value}
					target={openInNewTab ? '_blank' : '_self'}
					rel="nofollow"
					variant={index == 0 ? 'primary' : 'default'}
					className={classNames(styles['menu-item-link'])}
					icon={icon}
				>
					{label}
				</ButtonLink>
			))}
		</div>
	);
};

type ContentOptions = {
	menuContents: NavBarItem[];
	ctaLinks: MenuLink[];
	activeItemId: string;
	header?: ReactNode;
	isOpen?: boolean;
	onSelect: (id: string) => void;
};

const renderDesktopContent = ({
	ctaLinks,
	header,
	menuContents,
}: ContentOptions) => {
	return (
		<div className={styles['top-nav-primary-content']}>
			<DesktopNav items={menuContents} />
			{header && <div className={styles['top-nav-header']}>{header}</div>}
			{renderDesktopCTA(ctaLinks)}
		</div>
	);
};

const renderMobileMenu = (
	menuRef: RefObject<HTMLUListElement>,
	menuContents: NavBarItem[],
	ctaLinks: MenuLink[]
) => {
	return (
		<ul ref={menuRef} className={styles['top-nav-mobile-menu']}>
			<MobileNav items={menuContents} />
			{ctaLinks.map((ctaLink, index) => (
				<li
					key={ctaLink.path}
					className={classNames(
						!!menuContents.length && index == 0 ? styles['first-cta'] : ''
					)}
				>
					<Link
						to={ctaLink.value}
						target={ctaLink.openInNewTab ? '_blank' : '_self'}
						className={styles['mobile-cta-link']}
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: '4px',
						}}
					>
						{ctaLink.icon && (
							<StandaloneIcon title={ctaLink.label} icon={ctaLink.icon} />
						)}
						{ctaLink.label}
					</Link>
				</li>
			))}
		</ul>
	);
};

type TopNavProps = {
	activePath: string;
	menuContent: NavBarItem[];
	ctaLinks: MenuLink[];
	logo: Logo;
};

const TopNav = ({ menuContent, ctaLinks, logo }: TopNavProps) => {
	const displayType = useDisplayType();
	const isMinDesktop = displayType === 'desktop';

	const [open, setOpen] = useState(false);
	const [activeItemId, setActiveItemId] = useState<string>('');
	const menuRef = useRef<HTMLUListElement>(null);
	const toggleButtonRef = useRef<HTMLDivElement>(null);

	const handleMobileMenuToggled = () => {
		setOpen((currentState) => !currentState);
	};

	return (
		<header className={styles['top-nav']}>
			<div className={styles['top-nav-content']}>
				<div className={styles['top-nav-logo']}>
					<a href={logo.href}>
						<img
							src={logo.svg}
							alt={logo.alt}
							className={styles['logo']}
							width={logo.width}
							height={logo.height}
						/>
					</a>
				</div>
				{isMinDesktop ? (
					renderDesktopContent({
						menuContents: menuContent,
						ctaLinks,
						activeItemId,
						isOpen: open,
						onSelect: (value: string) => {
							setActiveItemId(value);
							setOpen(value !== ''); // if a non-empty value came back, the menu should be open
						},
					})
				) : (
					<TopNavBarMobile
						toggleButtonRef={toggleButtonRef}
						isOpen={open}
						onSelect={handleMobileMenuToggled}
					/>
				)}
			</div>
			{!isMinDesktop &&
				open &&
				renderMobileMenu(menuRef, menuContent, ctaLinks)}
		</header>
	);
};

export { TopNav };
